import type { AreaDemographics, CommunePoint } from "./demographics";
import type { AreaRisks, ClayReading, PointRisks } from "./risks";
import type { AreaClimate, AreaFires, HeatIsland, PointClimate } from "./climate";
import type { Level, StatsTable } from "./stats";
import { useAuthStore } from "../state/useAuthStore";

export interface YearStat {
  year: number;
  n: number;
  median_m2: number;
  p25_m2: number;
  p75_m2: number;
  median_surface: number;
  median_price: number;
}

export interface TimeSeries {
  scope: string;
  code: string;
  years: YearStat[];
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path);
  // Every endpoint here is behind a session, so a 401 is never about this
  // request in particular — the session has expired or been revoked, and the
  // app has to fall back to the login screen rather than showing a panel full
  // of failed queries.
  if (res.status === 401) {
    useAuthStore.getState().markAnonymous();
    throw new Error(`${path} → 401`);
  }
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

export const fetchTimeSeries = (scope: string, code: string) =>
  get<TimeSeries>(`/api/timeseries/${scope}/${encodeURIComponent(code)}`);

export interface NearbyStop {
  name: string;
  mode: string;
  calls: number;
  lines: string;
  metres: number;
  walk_min: number;
}

export const fetchNearbyStops = (lon: number, lat: number) =>
  get<{ stops: NearbyStop[] }>(
    `/api/nearby-stops?lon=${lon.toFixed(5)}&lat=${lat.toFixed(5)}`,
  );

export interface GeocodeHit {
  label: string;
  lon: number;
  lat: number;
  postcode?: string;
  city?: string;
  score: number;
}

/**
 * Geocode via the Base Adresse Nationale — the official French address
 * database. Public, keyless, and CORS-enabled, so the browser calls it
 * directly rather than proxying through our API.
 *
 * `near` biases results towards a point, which matters more than it sounds:
 * unbiased, "20 rue de Rivoli" returns Lille and Tourcoing above Paris, and
 * you have to type half a postcode to dig the right one out. Biased to where
 * the map is looking, the first hit is usually the one you meant.
 *
 * The signal aborts a request whose answer nobody wants any more, so a slow
 * reply for "rue de r" cannot land after a fast one for "rue de rivoli".
 */
export async function geocode(
  q: string,
  opts: { near?: { lon: number; lat: number }; signal?: AbortSignal } = {},
): Promise<GeocodeHit[]> {
  if (q.trim().length < 3) return [];
  const params = new URLSearchParams({ q, limit: "5" });
  if (opts.near) {
    params.set("lon", opts.near.lon.toFixed(4));
    params.set("lat", opts.near.lat.toFixed(4));
  }
  const res = await fetch(
    `https://api-adresse.data.gouv.fr/search/?${params}`,
    { signal: opts.signal },
  );
  if (!res.ok) throw new Error(`geocode → ${res.status}`);
  const data = (await res.json()) as {
    features: {
      geometry: { coordinates: [number, number] };
      properties: {
        label: string;
        postcode?: string;
        city?: string;
        score: number;
      };
    }[];
  };
  return data.features.map((f) => ({
    label: f.properties.label,
    lon: f.geometry.coordinates[0],
    lat: f.geometry.coordinates[1],
    postcode: f.properties.postcode,
    city: f.properties.city,
    score: f.properties.score,
  }));
}

export interface SiteReading {
  lon: number;
  lat: number;
  green: { score: number; green_pct: number; park_m: number } | null;
  air_noise: { air: number; noise: number } | null;
  /** Clay shrink-swell class. `null` means none is mapped here, not unknown. */
  clay: ClayReading | null;
  heat_island: HeatIsland | null;
  stops: NearbyStop[];
}

export const fetchSite = (lon: number, lat: number) =>
  get<SiteReading>(`/api/site?lon=${lon.toFixed(5)}&lat=${lat.toFixed(5)}`);

export interface PropertyTax {
  code: string;
  name: string;
  /** Total TFB rate voted for this commune — exact, from the DGFiP REI. */
  rate: number;
  /** Estimated taxable base per m² of living space. */
  base_per_m2: number;
  /** Estimated annual tax per m². */
  tax_per_m2: number;
  median_surface?: number;
  source_year: number;
  /** False for a department, where the figures are a median over its communes. */
  exact: boolean;
}

export const fetchPropertyTax = (code: string) =>
  get<PropertyTax>(`/api/property-tax/${encodeURIComponent(code)}`);

/**
 * Census series for an area, plus the department trajectory its future is hung
 * off. A neighbourhood is answered with its commune — the census does not
 * publish a consistent IRIS history — and says so in `resolved_from`.
 */
export const fetchDemographics = (scope: string, code: string) =>
  get<AreaDemographics>(
    `/api/demographics/${scope}/${encodeURIComponent(code)}`,
  );

/**
 * Every commune as one point: recent demographic change against price level
 * and price trend. Fetched whole and cached, because the panel re-plots the
 * same thousand points every time the metric, axis or department changes.
 */
export const fetchCorrelation = () =>
  get<{ census_year: number; communes: CommunePoint[] }>(
    "/api/demographics/correlation",
  );

/**
 * One area's hazard record: CatNat declarations since 1982, radon and seismic
 * zoning, PPR coverage. A neighbourhood is answered with its commune, because
 * hazard procedures are declared per commune and there is nothing finer.
 */
export const fetchRisks = (scope: string, code: string) =>
  get<AreaRisks>(`/api/risks/${scope}/${encodeURIComponent(code)}`);

/**
 * Everything hazardous about one coordinate.
 *
 * `clay` is baked and always answers. The three counts are proxied live from
 * Géorisques and are `null` when it is unreachable — which the panel has to
 * render as "unavailable", not as "none".
 */
export const fetchPointRisks = (lon: number, lat: number, radius: number) =>
  get<PointRisks>(
    `/api/risks/point?lon=${lon.toFixed(5)}&lat=${lat.toFixed(5)}&radius=${radius}`,
  );

/**
 * One area's climate reference and every scenario-horizon at once. Twelve rows
 * of a dozen numbers is small enough that shipping the lot beats putting a
 * round trip behind every flick of a scenario control.
 */
export const fetchClimate = (scope: string, code: string) =>
  get<AreaClimate>(`/api/climate/${scope}/${encodeURIComponent(code)}`);

/** The heat-island block under one coordinate, and today's water restriction. */
export const fetchPointClimate = (lon: number, lat: number) =>
  get<PointClimate>(
    `/api/climate/point?lon=${lon.toFixed(5)}&lat=${lat.toFixed(5)}`,
  );

/**
 * Recorded forest fires for an area.
 *
 * Separate from the climate series because it answers a different question at a
 * different geography — a projection describes an 8 km model cell, a fire is an
 * event attributed to the commune it started in.
 */
export const fetchFires = (scope: string, code: string) =>
  get<AreaFires>(`/api/climate/fires/${scope}/${encodeURIComponent(code)}`);

/**
 * Every zone at one geography, with every measure defined for it.
 *
 * Sent whole and cached for the session. The panel re-asks its question on
 * every change of axis, geography or filter, and all of those are arithmetic
 * over the same table rather than a new question for the server.
 */
export const fetchStats = (level: Level) => get<StatsTable>(`/api/stats/${level}`);
